import { differentFromOther } from "../../utils/constants";
import { DifferentSubComponentProps } from "../../utils/interfaces";
import { motion, useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { FiCheckCircle } from "react-icons/fi";

const HowWeDifferent = () => {
  return (
    <div
      className="relative py-8 500:py-12 px-p83 bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 grid gap-6 500:gap-8 grid-cols-1 950:grid-cols-2 items-center min-h-[90vh] overflow-hidden"
      style={{
        backgroundImage: 'radial-gradient(circle at 0% 50%, rgba(0, 120, 209, 0.1), transparent 50%)'
      }}
      id="different"
    >
      <motion.div
        className="flex justify-center gap-6 500:gap-8"
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex flex-col gap-6">
          <DifferentSubComponent
            years={5}
            text="Years of Experience"
            sign="+"
            delay={0}
          />
          <DifferentSubComponent
            years={45}
            text="Project Served"
            sign="+"
            delay={0.1}
          />
        </div>
        <div className="flex flex-col gap-6 pt-12 500:pt-20">
          <DifferentSubComponent
            years={12}
            text="Team Member"
            sign="+"
            delay={0.2}
          />
          <DifferentSubComponent
            years={96}
            text="Customer Retention Rate"
            sign="%"
            delay={0.3}
          />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <div className="gap-2 font-merry-bold tracking-tight flex flex-wrap text-xl 400:text-2xl 500:text-3xl pb-6">
          <p>
            How We
            <span className="text-primary-500 font-bold px-2">
              Different From Other{" "}
              <span className="whitespace-nowrap">Agency?</span>
            </span>
          </p>
        </div>
        <div className="grid gap-4 500:gap-6">
          {differentFromOther.map(({ heading, paragraph }, id) => (
            <motion.div
              key={id}
              className="flex gap-4 items-start group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: id * 0.1 }}
            >
              <motion.div
                className="mt-1 flex-shrink-0"
                whileHover={{ scale: 1.2, rotate: 90 }}
                transition={{ duration: 0.3 }}
              >
                <FiCheckCircle className="w-6 h-6 500:w-7 500:h-7 text-primary-500" />
              </motion.div>
              <div className="flex-1">
                <h3 className="text-base 400:text-lg 500:text-xl font-poppins-semi-bold text-neutral-100 mb-1.5 group-hover:text-primary-400 transition-colors duration-300">
                  {heading}
                </h3>
                <p className="text-neutral-300 font-poppins-regular text-xs 400:text-sm 500:text-base leading-relaxed">
                  {paragraph}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default HowWeDifferent;

const DifferentSubComponent = ({
  years,
  text,
  sign,
  delay = 0,
}: DifferentSubComponentProps & { delay?: number }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  // Define color schemes for each box
  const colorSchemes = [
    {
      bg: "from-primary-500/20 to-primary-600/10",
      border: "border-primary-500/50",
      hoverBg: "hover:from-primary-500 hover:to-primary-600",
      text: "text-primary-400",
      hoverBorder: "hover:border-primary-400",
    },
    {
      bg: "from-accent-orange/20 to-accent-orange/10",
      border: "border-accent-orange/50",
      hoverBg: "hover:from-accent-orange hover:to-accent-orange/90",
      text: "text-accent-orange",
      hoverBorder: "hover:border-accent-orange",
    },
    {
      bg: "from-emerald-500/20 to-emerald-600/10",
      border: "border-emerald-500/50",
      hoverBg: "hover:from-emerald-500 hover:to-emerald-600",
      text: "text-emerald-400",
      hoverBorder: "hover:border-emerald-400",
    },
    {
      bg: "from-purple-500/20 to-purple-600/10",
      border: "border-purple-500/50",
      hoverBg: "hover:from-purple-500 hover:to-purple-600",
      text: "text-purple-400",
      hoverBorder: "hover:border-purple-400",
    },
  ];

  // Use index based on years to assign colors (or use a hash)
  const colorIndex = [5, 45, 12, 96].indexOf(years);
  const colors = colorSchemes[colorIndex] || colorSchemes[0];

  useEffect(() => {
    if (isInView) {
      const duration = 2000;
      const steps = 60;
      const increment = years / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= years) {
          setCount(years);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);
      return () => clearInterval(timer);
    }
  }, [isInView, years]);

  return (
    <motion.div
      ref={ref}
      className={`text-center my-5 transition-all duration-500 bg-gradient-to-br ${colors.bg} shadow-xl hover:shadow-2xl 515:w-[210px] 515:h-[210px] 350:w-[150px] 350:h-[150px] 300:w-[130px] 300:h-[130px] w-[100px] h-[100px] grid items-center rounded-3xl border-2 ${colors.border} ${colors.hoverBg} ${colors.hoverBorder} hover:text-white group cursor-pointer backdrop-blur-sm`}
      initial={{ opacity: 0, scale: 0.8 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ scale: 1.05, y: -5 }}
    >
      <span>
        <p className={`515:text-6xl font-poppins-bold tracking-tight 300:text-4xl text-2xl font-bold ${colors.text} group-hover:text-white transition-colors duration-300`}>
          {isInView ? count : 0}
          {sign}
        </p>
        <p className="515:text-lg font-poppins-medium tracking-tight 300:text-base text-xs p-2 text-neutral-300 group-hover:text-white transition-colors duration-300">
          {text}
        </p>
      </span>
    </motion.div>
  );
};
